import {
  type AgentFilter,
  type AgentUpsertRequest,
  type AgentResponse,
  AgentFilterPropertyTypesItem,
  AgentFilterStatusItem,
  MunicipalitiesEnum,
  RegionsEnum,
} from "~/api/generated/model";

export type SanitizedSearchAgent = Omit<
  AgentResponse,
  "agentSearchCriteria"
> & {
  filter: AgentUpsertRequest["filter"];
};

export type SanitizedSearchAgentProspect = Omit<
  SanitizedSearchAgent,
  "id" | "createdAt" | "modifiedAt" | "matchCount" | "newMatchCount"
>;

/**
 * Sanitized agents, to ensure values returned are in line with the current frontend definitions,
 * but also to streamline the `filter` / `agentSearchCriteria` returned from the API.
 */
export const sanitizeAgents = (
  agents: AgentResponse[],
): SanitizedSearchAgent[] => agents.map(sanitizeAgent);

/**
 * Sanitized agent, to ensure values returned are in line with the current frontend definitions,
 * but also to streamline the `filter` / `agentSearchCriteria` returned from the API.
 */
export const sanitizeAgent = (agent: AgentResponse): SanitizedSearchAgent => {
  const { agentSearchCriteria, ...partialAgent } = agent;

  return {
    ...partialAgent,
    filter: sanitizeFilter(agentSearchCriteria),
  };
};

export const sanitizeFilter = (search: AgentFilter) => {
  const filter: SanitizedSearchAgent["filter"] = {
    ...search,
    regions: search.regions?.filter((region) => REGION_VALUES.includes(region)),
    municipalities: search.municipalities?.filter((municipality) =>
      MUNICIPALITIES_VALUES.includes(municipality),
    ),
    status: search.status?.filter((status) => STATUS_VALUES.includes(status)),
    propertyTypes: search.propertyTypes?.filter((propertyType) =>
      PROPERTY_TYPE_VALUES.includes(propertyType),
    ),
  };

  if (!filter.regions?.length) {
    delete filter.regions;
  }
  if (!filter.municipalities?.length) {
    delete filter.municipalities;
  }
  if (!filter.status?.length) {
    delete filter.status;
  }
  if (!filter.propertyTypes?.length) {
    delete filter.propertyTypes;
  }

  if (typeof filter.builderOrDeveloper !== "boolean") {
    delete filter.builderOrDeveloper;
  }

  // The filter panel can only hide imprecise proposals, not show them alone.
  if (filter.preciseLocation !== true) {
    delete filter.preciseLocation;
  }

  return filter;
};

const REGION_VALUES = Object.values(RegionsEnum);
const MUNICIPALITIES_VALUES = Object.values(MunicipalitiesEnum);

const STATUS_VALUES = Object.values(AgentFilterStatusItem);

const PROPERTY_TYPE_VALUES = Object.values(AgentFilterPropertyTypesItem);
